import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

import { Filter, FilterId, FilterType } from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { CombinedNode } from "@/pages/scenario-builder/utils/nodes";
import { getMappedName } from "@/Utils/mapping-utils";

import { useCampaigns } from "./api/getCampaigns";
import { useCampaignsStore } from "./store/useCampaignsStore";
export const useAvailableFilters = ({
  selectedNode,
  startDate,
  endDate,
}: {
  selectedNode?: CombinedNode;
  startDate?: string;
  endDate?: string;
}) => {
  const { setTargetNode, setChannels, setCampaigns, setGeographies } = useCampaignsStore();
  const { targetNode, channels, geographies, campaigns } = useCampaignsStore(
    (state) => state.filters
  );
  const { initInfo: info } = useInfo();
  const [geoData] = info?.geographies || [];

  const { data: allCampaigns } = useCampaigns({
    startDate,
    endDate,
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
  });

  const handleTargetNodeChange = useCallback(
    (targetNode: string | null) => {
      setTargetNode(targetNode);
    },
    [setTargetNode]
  );
  const handleChannelsChange = useCallback(
    (channel: string[]) => setChannels(channel),
    [setChannels]
  );

  const handleCampaignsChange = useCallback(
    (campaign: string[]) => setCampaigns(campaign),
    [setCampaigns]
  );

  const handleGeographiesChange = useCallback(
    (geography: string[]) => setGeographies(geography),
    [setGeographies]
  );

  const campaignOptions = useMemo(() => {
    if (!allCampaigns) return [];

    return allCampaigns
      .map((campaign) => ({
        label: getMappedName(campaign) || campaign,
        value: campaign,
        selected: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allCampaigns]);

  const targetNodeFilter = useMemo(
    () => ({
      id: FilterId.BusinessUnit,
      label: "Business Unit",
      type: FilterType.BusinessUnit,
      value: targetNode,
      onChange: handleTargetNodeChange,
    }),
    [targetNode, handleTargetNodeChange]
  );

  const channelsFilter = useMemo(
    () => ({
      id: FilterId.Channel,
      label: "Channels",
      type: FilterType.MultiSelect,
      value: channels,
      onChange: handleChannelsChange,
    }),
    [channels, handleChannelsChange]
  );

  const campaignsFilter = useMemo(
    () => ({
      id: FilterId.Campaign,
      label: "Campaigns",
      type: FilterType.MultiSelect,
      value: campaigns ?? [],
      onChange: handleCampaignsChange,
      options: campaignOptions,
    }),
    [campaigns, handleCampaignsChange, campaignOptions]
  );

  const geographiesFilter = useMemo(
    () => ({
      id: FilterId.Geography,
      label: "Geo",
      type: FilterType.MultiSelect,
      value: geographies,
      onChange: handleGeographiesChange,
    }),
    [geographies, handleGeographiesChange]
  );

  useEffect(() => {
    if (!isEmpty(allCampaigns)) {
      setCampaigns(allCampaigns || []);
    }
  }, [allCampaigns, setCampaigns]);

  return useMemo(() => {
    const filters: Filter[] = [targetNodeFilter, channelsFilter, campaignsFilter];
    if (geoData && geoData.length > 0) {
      filters.push(geographiesFilter);
    }

    return filters;
  }, [targetNodeFilter, channelsFilter, geographiesFilter, geoData, campaignsFilter]);
};
